<template>
  <article>
    <Hero heading="NACS/J3400 Pilot Charging Stations">
      <template slot="image">
        <picture>
          <img
            :src="
              !isMobile
                ? require('@/assets/images/NACSPilot/hero.jpg')
                : require('@/assets/images/NACSPilot/hero-mobile.jpg')
            "
            alt="EV Charger with NACS prominently displayed at the top of the unit."
          />
        </picture>
      </template>
    </Hero>

    <section class="l-one bg-gray">
      <div class="l-one__col l-one__col--wide text-center">
        <h2 class="headline3">Meeting the Needs of Our EV Community</h2>
        <p>
          As electric vehicle (EV) charging continues to evolve, Electrify America is expanding NACS/J3400 support
          across our Hyper-Fast network, while continuing to support CCS drivers. Through a pilot approach, we’re able
          to test and learn to inform our broader NACS rollout strategy.
        </p>
      </div>
    </section>

    <section class="l-one">
      <div class="l-one__col l-one__col--wide text-center">
        <div class="icon-header">
          <img src="@/assets/images/NACSPilot/charger-icon.png" alt="Charger" class="charger-icon" />
          <h2 class="headline3">Pilot Station Locations</h2>
        </div>
        <p>NACS connectors will be available at the stations below across our network.</p>

        <div class="column" v-if="!isMobile">
          <img
            src="@/assets/images/NACSPilot/map.png"
            alt="Map of the United States with a pin on the listed stations"
            class="map-image"
          />
        </div>
      </div>
      <div class="l-two-col">
        <div class="third-column">
          <div class="column-header">
            <img src="@/assets/images/NACSPilot/location-pin.png" alt="Location Pin" class="pin-icon" />
            <div>
              <h3>Live Now</h3>
            </div>
          </div>
          <div>
            <strong>Waterford Commons</strong><br />
            <span class="address">Waterford, CT (Station ID: 100286)</span><br />
            <strong>The Florida Mall </strong><br />
            <span class="address">Orlando, FL (Station ID: 100255)</span><br />
            <strong>Harrison Street Flagship</strong><br />
            <span class="address">San Francisco, CA (Station ID: 219001)</span><br />
            <strong>Fashion Valley Mall South</strong><br />
            <span class="address">South San Diego, CA (Station ID: 219003)</span><br />
            <strong>Santa Monica Station</strong><br />
            <span class="address">Santa Monica, CA (Station ID: 219009)</span><br />
            <strong>Westfield Valley Fair</strong><br />
            <span class="address">Santa Clara, CA (Station ID: 210061)</span><br />
            <strong>Carrillo Street Station</strong><br />
            <span class="address">Santa Barbara, CA (Station ID: 219002)</span><br />
            <strong>Albertsons 0561 </strong><span class="tag">Fully NACS</span><br />
            <span class="address">Arcadia, CA (Station ID: 200050)</span><br />
            <strong>Bank of America 17th-Tustin CA7 133</strong><span class="tag">Fully NACS</span><br />
            <span class="address">Santa Ana, CA (Station ID: 200067)</span><br />
            <strong>La Mirada</strong><span class="tag">Fully NACS</span><br />
            <span class="address">La Mirada, CA (Station ID: 200075)</span><br />
            <strong>Target T2179</strong><span class="tag">Fully NACS</span><br />
            <span class="address">Diamond Bar, CA (Station ID: 200052)</span>
          </div>
        </div>
        <div class="third-column">
          <div class="column-header">
            <img src="@/assets/images/NACSPilot/summer-pin.png" alt="Location Pin" class="pin-icon" />
            <div>
              <h3>Fall 2026</h3>
            </div>
          </div>
          <div>
            <p>
              Select stations will be fully converted to NACS chargers, while others will convert only some chargers to
              support NACS vehicles.
            </p>
            <strong>Wakefern Shoprite 140</strong><br />
            <span class="address">Wyckoff, NJ (Station ID: 240370)</span><br />
            <strong>Wakefern Shoprite 111</strong><br />
            <span class="address">Nutley, NJ (Station ID: 240431)</span><br />
            <strong>Wakefern Shoprite 407 </strong><br />
            <span class="address">Yardley, PA (Station ID: 240432)</span><br />
            <strong>Wawa Store 0219</strong><br />
            <span class="address">Bristol, PA (Station ID: 240504)</span>
          </div>
        </div>
      </div>
    </section>

    <section class="l-one bg-gray">
      <Card headlineLevel="h2" heading="What To Expect" :maxWidth="1200" bgColor>
        <template #image>
          <img
            loading="lazy"
            style="max-width: 520px; border-radius: 15px"
            v-if="!isMobile"
            src="@/assets/images/NACSPilot/what-to-expect.png"
            alt="The electrify america mobile app with an NACS labeled charger."
          />
          <img
            loading="lazy"
            v-if="isMobile"
            style="max-width: 100%; margin-top: 30px; border-radius: 15px"
            src="@/assets/images/NACSPilot/what-to-expect.png"
            alt="The electrify america mobile app with an NACS labeled charger."
          />
        </template>
        <div class="column-header">
          <img src="@/assets/images/NACSPilot/nacs-circle.png" alt="NACS Charger Icon" class="circle-icon" />
          <div>
            <p>Chargers equipped with a NACS connector will have a NACS symbol displayed in the app.</p>
          </div>
        </div>
        <div class="column-header">
          <img src="@/assets/images/NACSPilot/charger-circle.png" alt="Location Pin" class="circle-icon" />
          <div>
            <p>During the installation process, charger availability may be impacted.</p>
          </div>
        </div>
        <div class="column-header">
          <img src="@/assets/images/NACSPilot/lightbulb-circle.png" alt="Location Pin" class="circle-icon" />
          <div>
            <p>
              Refer to the
              <router-link :to="{ name: 'mobile-app-en' }" class="link">Electrify America app</router-link> or the
              <a href="https://bit.ly/network-upgrades-and-maintenance" target="_blank" class="link">Network Status</a>
              page for updates.
            </p>
          </div>
        </div>
        <div class="column-header">
          <img src="@/assets/images/NACSPilot/phone-circle.png" alt="Location Pin" class="circle-icon" />
          <div>
            <p>
              New to Electrify America? Visit our
              <router-link :to="{ name: `charging-your-ev-en` }" class="link">Charging Your EV</router-link> page for
              more information.
            </p>
          </div>
        </div>
      </Card>
    </section>

    <section class="l-one">
      <div class="l-one__col l-one__col--wide">
        <h2 class="headline3">Frequently Asked Questions (FAQs)</h2>
        <p>
          Get answers to some of the most commonly asked questions about our pilot stations featuring NACS/J3400
          connectors.
        </p>
        <accordion>
          <accordion-item title="Is Electrify America switching entirely to NACS right now?">
            <p>
              This is a limited NACS pilot. We will continue to support the many CCS-native vehicles on the road today.
              This phased approach to NACS is an opportunity for us to learn and scale thoughtfully while continuing to
              support drivers across our network.
            </p>
          </accordion-item>
          <accordion-item title="How can I tell which chargers have NACS connectors?">
            <div class="flex">
              <div>
                <p>
                  The Electrify America chargers with a NACS connector will have a triangular NACS label at the top of
                  the charger.
                </p>
                <p>
                  You can also find out which chargers are NACS-compatible by looking up the station in the Electrify
                  America app and tapping the “Charging” tab, or by searching the station on Google Maps. Your vehicle’s
                  navigation app may also display CCS and NACS availability at our stations.
                </p>
              </div>
              <img
                loading="lazy"
                class="image"
                src="@/assets/images/NACSPilot/faq-info.png"
                alt="Diagram showing how to tap a smartphone to the charging station’s RFID reader."
              />
            </div>
          </accordion-item>
          <accordion-item title="Will Electrify America support both styles of connectors long term?">
            <p>
              Electrify America is focused on supporting drivers today while preparing for the future. Our network is
              designed to evolve over time, and we expect to continue supporting both CCS and NACS as we learn from this
              pilot.
            </p>
          </accordion-item>
          <accordion-item title="How many chargers at a station will have NACS?">
            <p>
              It varies by station. To find the total number of NACS and CCS connectors at your station, select the
              station in the Electrify America app and tap the “Charging” tab.
            </p>
          </accordion-item>
          <accordion-item title="Can I use my Electrify America app to charge at these stations?">
            <p>
              Yes! The Electrify America app can be used to start and manage your charging session, just like at any
              other Electrify America location.
            </p>
          </accordion-item>
          <accordion-item title="Can I use an adapter at these stations?">
            <p>
              Electrify America only permits the use of adapters that are UL 2252 certified and authorized by the
              vehicle's automaker for the specified vehicle. Please review Electrify America’s
              <router-link :to="{ name: 'terms-en' }" class="link">Terms of Use</router-link>
              for further guidance.
            </p>
          </accordion-item>
        </accordion>
      </div>
    </section>
  </article>
</template>

<script>
import Accordion from '@/components/Accordion/Accordion.vue';
import AccordionItem from '@/components/Accordion/AccordionItem.vue';
import Card from '@/components/Card/Card.vue';
import Hero from '@/components/Hero/Hero.vue';

export default {
  name: 'getting-started',
  components: { Accordion, AccordionItem, Card, Hero },
  metaInfo: {
    title: `NACS Pilot Stations | Electrify America`,
    meta: [
      {
        name: 'description',
        content: `Available in Orlando, FL and Waterford, CT, our pilot stations featuring both NACS/J3400 and CCS connectors provide Hyper-Fast charging speeds`,
      },
    ],
    link: [{ rel: 'canonical', href: 'https://www.electrifyamerica.com/pilot-stations/' }],
  },
  computed: {
    isMobile() {
      return this.$resize && this.$mq.below(720);
    },
  },
};
</script>

<style lang="scss" scoped>
.bg-gray {
  background: $c-secondary-background;
}
.l-one {
  padding-top: 80px;
  padding-bottom: 80px;

  .text-center {
    text-align: center;
  }
}

.headline3 {
  padding-bottom: 16px;
  align-content: center;
}

.pilot-station {
  display: flex;
  justify-content: center;
  align-items: center;
}

.header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}
.icon-header {
  display: flex;
  flex-direction: row;
  justify-content: center;
  .charger-icon {
    width: 50px;
    padding-right: 10px;
  }
  .headline3 {
    padding-bottom: 0px;
  }
}
.column-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
}

.pin-icon {
  width: 25px;
  margin-right: 10px;
  margin-top: 5px;
}

.circle-icon {
  width: 50px;
  margin: auto;
  padding-right: 15px;
}

.map-image {
  max-width: 100%;
}

.l-two-col {
  flex-direction: row;
  gap: 25px;

  @media (max-width: 750px) {
    flex-direction: column;
    width: fit-content;
    gap: 0px;
  }
  .third-column {
    flex: 1;
    margin-top: 50px;

    p {
      max-width: 80%;

      @media (max-width: 750px) {
        max-width: 100%;
      }
    }
  }
}
.address {
  color: gray;
}

.flex {
  flex-direction: column;
  @media (min-width: 1200px) {
    flex-direction: row;
  }

  img {
    margin-left: 25px;
    max-width: 600px;

    @media (max-width: 1200px) {
      flex-direction: row;
      margin: auto;
      max-width: 90%;
    }
  }
}

.tag {
  margin-left: 5px;

  padding: 0.2rem 0.8rem;
  border-radius: 9999px;
  background-color: #e5e5e7;
  white-space: nowrap;

  font-size: 0.85rem;
  font-weight: 550;
}
</style>
